import React, { lazy, useEffect, useState } from 'react';
import {
  Switch,
  useLocation,
  useParams,
  useHistory,
  Route,
} from 'react-router-dom';
import { Location } from 'history';
import { LoadingPage } from 'components/loading';
import { routes as getRoutes, copyFor } from 'config';
import { usePageDisplay, DisplayData } from 'hooks/use-page-display.hook';
import { usePlanInactive } from 'hooks/use-plan-inactive.hook';
import { SubNav } from 'components/navigation';
import { ErrorPage } from 'components/errors';
import { InactivePlanBanner } from 'components/inactive-plan-banner';
import { useTrackPage } from 'hooks/use-track-page.hook';
import { mapPages } from 'utils/map-pages';
import { withSuspense } from 'utils/with-suspense';
import { NetworkStatus } from '@apollo/client';

const getCopy = copyFor('navigation.navBar.links');
const Usage = lazy(() => import('pages/usage'));
const Solar = lazy(() => import('pages/solar'));
const Billing = lazy(() => import('pages/billing'));

const pageMap = {
  billing: () => withSuspense(Billing),
  solar: () => withSuspense(Solar),
  usage: () => withSuspense(Usage),
};

export const DashboardPlanPage = () => {
  const history = useHistory();
  const { planId } = useParams<{ planId?: string }>();
  const { data } = usePageDisplay({
    planId: Number(planId),
  });

  const routes = getRoutes({ planId });

  useEffect(() => {
    if (data) {
      if (data.displayBilling) {
        history.replace(routes.billing);
      } else {
        history.replace(routes.solar);
      }
    }
  }, [data, routes, history]);

  return <LoadingPage />;
};

export const DashboardPlan = () => {
  useTrackPage();
  const { planId } = useParams<{ planId?: string }>();
  const { data, error, loading } = usePageDisplay({
    planId: Number(planId),
  });
  const planInactive = usePlanInactive({
    planId: Number(planId),
  });
  const location = useLocation();
  const [refreshOnReactivation, setRefreshOnReactivation] = useState(false);

  const routes = getRoutes({ planId });

  useEffect(() => {
    planInactive.refetch();
  }, [refreshOnReactivation, planInactive.data?.planInactive]);

  const subNavRoutes = [routes.billing, routes.solar, routes.usage];
  const showSubNav = (location: Location<unknown>) =>
    subNavRoutes.includes(location.pathname);

  const routeTemplates = getRoutes();
  const getDashboardPages = (data?: DisplayData) => ({
    billing: {
      display: data?.displayBilling,
      route: routeTemplates.billing,
      title: getCopy('billing'),
    },
    solar: {
      display: true,
      route: routeTemplates.solar,
      title: getCopy('solar'),
    },
    usage: {
      display: data?.displayUsage,
      route: routeTemplates.usage,
      title: getCopy('usage'),
    },
  });

  if (
    loading ||
    planInactive.loading ||
    planInactive.networkStatus === NetworkStatus.refetch
  )
    return <LoadingPage />;
  if (error || planInactive.error) return <ErrorPage />;

  const dashboardPages = getDashboardPages(data);
  const shouldShowSubNav = showSubNav(location);

  // Pages that are not part of the sub navigation are not Arcadia account-specific, so we don't want to show the closed banner
  const displayClosedBanner =
    shouldShowSubNav && planInactive.data?.planInactive;

  return (
    <>
      {shouldShowSubNav && <SubNav />}
      {displayClosedBanner && (
        <InactivePlanBanner
          showReactivateButton={
            planInactive.data?.planInactive &&
            !planInactive.data?.isMembershipPlan &&
            planInactive.data?.reactivationEligibility.eligible
          }
          planId={Number(planId)}
          planName={planInactive.data?.displayName}
          onRefresh={() => setRefreshOnReactivation(!refreshOnReactivation)}
        />
      )}
      <Switch>
        {mapPages({ pages: dashboardPages, pageMap })}
        <Route
          path={routeTemplates.dashboardPlan}
          component={DashboardPlanPage}
        />
      </Switch>
    </>
  );
};
